import * as dm from '../domain';
import BaseView, { DomainModel3D } from './base-view';
import ClosetUnitView from './closet-unit-view';
import CubbyView from './cubby-view';
import DoorView from './door-view';
import DrawerView from './drawer-view';
import GenericAssetView from './generic-asset-view';
import HookRackView from './hook-rack-view';
import Marker3DView from './marker-3d-view';
import MurphybedView from './murphybed-view';
import ParametricPartView from './parametric-part-view';
import SectionView from './section-view';
import SlantedShelfView from './slanted-shelf-view';
import VerticalPanelView from './vertical-panel-view';
import mfac from '../material-factory';
import pc from '../parametric-cache';
import { BoxGeometry, BufferGeometry, Color, Group, Material, Mesh, MeshBasicMaterial, MeshPhongMaterial, MeshStandardMaterial, Object3D, } from 'three';
import { WallViewConfig, wallViewDefaults, WallViewOptions } from './views-config';
import { partition } from '../utils';
import { lerp } from '../math/lerp';
import { zip } from '../control-flow/zip';
import { pairwise } from '../control-flow/pairwise';
import { update, clone } from "../json/optional-properties";
import { wallLength } from '../controllers/wall-controllers';


type SpaceProperties = {
    start: number;
    width: number;
    open: boolean;
    extension: boolean;
}

// Red, shadeless material for the line dividing the wall from the extension
export const EXTENSION_LINE_MATERIAL = new MeshBasicMaterial({color: 0xE6160B});

let unitCubeGeometry: BufferGeometry;
const unitCube = (mat: Material) => {
    if(!unitCubeGeometry) {
        unitCubeGeometry = new BoxGeometry(1, 1, 1);
        unitCubeGeometry.translate(0.5, 0.5, 0.5);
    }
    return new Mesh(unitCubeGeometry, mat);
};

/**
 * Used internally. The important thing is definining the toString() method,
 * which allows us to directly pass a Space instance to the ParametricCache.
 * A plain object would have the cache key "[object Object]", so all spaces
 * would appear the same.
 */
class Space {
    start: number;
    width: number;
    open: boolean;
    extension: boolean;

    constructor(init: SpaceProperties) {
        this.start = init.start;
        this.width = init.width;
        this.open = init.open;
        this.extension = init.extension;
    }

    toString() {
        return `${this.start},${this.width},${this.open?1:0},${this.extension?1:0}`;
    }
}

export const getParentWallView = (view: BaseView): WallView | undefined => {
    let parent: Object3D | undefined = view;
    while(view != null) {
        if(parent instanceof WallView) {
            return parent;
        }
        parent = parent.parent;
    }
    return undefined;
}

export default class WallView extends BaseView {

    declare model: dm.Wall;

    leftExtensionLine?: Mesh;
    rightExtensionLine?: Mesh;

    spaces: Space[] = [];
    meshes: Mesh[] = [];

    offset: Group = new Group();

    config: WallViewConfig = wallViewDefaults;

    constructor(model: DomainModel3D, options: WallViewOptions) {
        super(model);

        update(this.config, options);
        this.config.standardHeights = clone(options.standardHeights);

        this.onReady.name = 'WallView';
        this.init();
    }

    make() {
        this.setupSpacesArray();

        for(let panel of this.model.panels) {
            this.add(new VerticalPanelView(
                panel,
                {
                    standardHeights: this.config.standardHeights,
                }));
        }

        this.add(this.offset);

        for(let unit of this.model.closetUnits) {
            this.add(new ClosetUnitView(unit, {
                standardHeights: this.config.standardHeights,
            }));
        }

        for(let door of this.model.doors) {
            this.add(new DoorView(door));
        }

        for(let drawer of this.model.drawers) {
            this.add(new DrawerView(drawer));
        }

        for(let asset of this.model.assets) {
            this.add(new GenericAssetView(asset));
        }

        for(let rack of this.model.hookRacks) {
            this.add(new HookRackView(rack));
        }

        for(let bed of this.model.beds) {
            this.add(new MurphybedView(bed));
        }

        for(let part of this.model.parametricParts) {
            this.add(new ParametricPartView(part));
        }

        for(let cubby of this.model.cubbies) {
            this.add(new CubbyView(cubby));
        }

        for(let section of this.model.sections) {
            this.add(new SectionView(section));
        }

        for(let marker of this.model.markers) {
            this.add(new Marker3DView(marker));
        }

        for(let shelf of this.model.slantedShelves) {
            this.add(new SlantedShelfView(shelf));
        }
    }

    bind() {
        this.buildGeometry = this.buildGeometry.bind(this);

        if(this.model.material.type == null) {
            this.model.material = dm.Material.fromJSON({
                type: "WallPaint",
            });
        }

        type Mat = MeshPhongMaterial | MeshStandardMaterial;
        const
            normalMat = mfac.makefrom(this.model.material) as Mat,
            openMat = (mfac.make('WallPaint') as Mat).clone(),
            extensionMat = mfac.make('WallExtension') as Mat,
            openExtensionMat = extensionMat.clone();
        openMat.color = openExtensionMat.color = new Color("#808080");

        this.model.material.bind('tint', () => {
            normalMat.color = new Color(this.model.material.tint);
            normalMat.userData.materialTint = normalMat.color.getHexString();
        });

        const updateMaterials = () => {
            for(let i = 0; i < this.meshes.length; i++) {
                const mesh = this.meshes[i], space = this.spaces[i];
                switch(true) {
                    case this.model.showOpenSpace && this.model.showExtension
                        && space.open && space.extension:
                        mesh.material = openExtensionMat;
                        break;
                    case this.model.showOpenSpace && space.open:
                        mesh.material = openMat;
                        break;
                    case this.model.showExtension && space.extension:
                        mesh.material = extensionMat;
                        break;
                    default:
                        mesh.material = normalMat;
                }
            }
        };
        const changeSize = () => {
            this.updates.queue(
                this.buildGeometry,
                (geos) => {
                    for(const [mesh, geo] of zip(this.meshes, geos)) {
                        mesh.geometry = geo;
                    }
                    updateMaterials();
                    this.onReady.trigger();
                }
            );
        };
        this.model.bind('showOpenSpace', changeSize);
        this.model.on('showOpenSpaceIndex', changeSize);
        this.model.on('showExtension', changeSize);

        this.model.on('depth', changeSize);
        this.model.on('heights', changeSize);

        this.model.bind('visible', () => {
            for(const mesh of this.meshes) {
                mesh.visible = this.model.visible;
            }
        });
    }

    async buildGeometry(): Promise<BufferGeometry[]> {
        // the heights we pass to ParametricCache
        const
            heights = this.model.heights.map(h => { return {x: h.x, height: h.height} }),
            flatHeights: number[] = [];

        this.setupSpacesArray();

        if(this.model.showExtension) {
            if(this.model.leftExtension > 0) {
                for(const height of heights) {
                    height.x += this.model.leftExtension;
                }
                heights.splice(0, 0, {x: 0, height: heights[0].height});
                if(!this.leftExtensionLine) {
                    this.leftExtensionLine = unitCube(EXTENSION_LINE_MATERIAL);
                }
                this.leftExtensionLine.position.set(
                    this.model.leftExtension - 0.25, 0, 0);
                this.leftExtensionLine.scale.set(
                    0.5, this.model.heights[0].height, 0.1);
                this.leftExtensionLine.removeFromParent();
                this.offset.add(this.leftExtensionLine);
            } else {
                if(this.leftExtensionLine) {
                    this.leftExtensionLine.removeFromParent();
                    this.leftExtensionLine = null;
                }
            }
            if(this.model.rightExtension > 0) {
                const last = heights[heights.length - 1];
                heights.push({x: last.x + this.model.rightExtension,
                             height: heights[heights.length - 1].height});
                if(!this.rightExtensionLine) {
                    this.rightExtensionLine = unitCube(EXTENSION_LINE_MATERIAL);
                }

                this.rightExtensionLine.position.set(
                    (this.model.leftExtension ?? 0) + wallLength(this.model) - 0.25,
                    0,
                    0);
                const h = this.model.heights;
                this.rightExtensionLine.scale.set(
                    0.5, h[h.length-1].height, 0.1);
                this.rightExtensionLine.removeFromParent();
                this.offset.add(this.rightExtensionLine);
            } else {
                if(this.rightExtensionLine) {
                    this.rightExtensionLine.removeFromParent();
                    this.rightExtensionLine = null;
                }
            }
        }

        if(this.config.hideStart) {
            const
                [left, right] = partition(heights, w => w.x < 4.5),
                a = left[left.length - 1],
                b = right[0],
                // 
                t = 4.5 / (b.x - a.x),
                height = lerp(a.height, b.height, t);
            flatHeights.push(4.5, height);
            for(const {x, height} of right) {
                flatHeights.push(x, height);
            }
        }
        else {
            for(const {x, height} of heights) {
                flatHeights.push(x, height);
            }
        }
        return await pc.make('Wall', flatHeights, this.spaces) as any as BufferGeometry[];
    }

    private setupSpacesArray () {
        // We build up a list of x coordinates where there are changes from open
        // to non-open or from extension to normal textures. Later we create spaces
        // that span the gaps between the members of the list.
        const
            model = this.model,
            spaces = this.spaces = [],
            length = wallLength(model),
            points = [0, length];
        if(model.showExtension) {
            if(model.rightExtension > 0) {
                // add the extended right end
                points.push(model.rightExtension + length);
            }
            if(model.leftExtension > 0) {
                for(let i=0; i < points.length; i++) {
                    points[i] += model.leftExtension;
                }
                // add the left extreme point
                points.push(0);
            }
        }
        // Note that points may contain duplicates, but we will sort it and ignore
        // intervals with zero widths
        if(model.showExtension) {
            this.offset.position.x = -model.leftExtension;
        }

        if(model.showOpenSpace) {
            const space = model.openSpaces[model.showOpenSpaceIndex];
            const start = space.start;
            points.push(start);
            points.push(start + space.width);
        }
        points.sort((a, b) => a - b);

        const
            leftEnd = model.leftExtension,
            rightEnd = leftEnd + length;
        for(const [a, b] of pairwise(points)) {
            const width = b - a;
            // checking the width effectively de-duplicates the points array
            if(width > 0) {
                spaces.push(new Space({
                    start: a,
                    width: width,
                    extension: a < leftEnd || a >= rightEnd,
                    open: false,
                }));
            }
        }

        if(model.showOpenSpace) {
            const openSpace = model.openSpaces[model.showOpenSpaceIndex];
            for(const space of spaces) {
                const
                    openStart = openSpace.start + model.leftExtension,
                    openEnd = openStart + openSpace.width,
                    spaceStart = space.start,
                    spaceEnd = spaceStart + space.width;
                if(spaceStart >= openStart && spaceEnd <= openEnd) {
                    space.open = true;
                } else if(space.start > openEnd) {
                    break;
                }
            }
        }

        while(this.spaces.length > this.meshes.length) {
            const mesh = new Mesh(new BufferGeometry(), new MeshStandardMaterial())
            mesh.castShadow = mesh.receiveShadow = true;

            this.meshes.push(mesh);
            this.offset.add(mesh);
        }
        if(this.meshes.length > this.spaces.length) {
            const removed = this.meshes.splice(this.spaces.length);
            for(const obj of removed) {
                obj.removeFromParent();
            }
        }
    }
}

